import type { FunctionComponent } from 'react';
import { useCallback } from 'react';

import { Analytics } from '@trello/atlassian-analytics';
import { useFeatureGate } from '@trello/feature-gate-client';
import { forTemplate } from '@trello/legacy-i18n';
import { GearIcon } from '@trello/nachos/icons/gear';
import { InformationIcon } from '@trello/nachos/icons/information';

import { LoadingSpinner } from 'app/src/components/LoadingSpinner';
import { Capabilities } from './Capabilities';
import { usePluginCapabilitiesQuery } from './PluginCapabilitiesQuery.generated';
import { Toggle } from './Toggle';
import { useUpdatePluginCapabilitiesMutation } from './UpdatePluginCapabilitiesMutation.generated';
import { useUpdatePluginCapabilitiesOptionsMutation } from './UpdatePluginCapabilitiesOptionsMutation.generated';
import { useSyncedState } from './useSyncedState';

import styles from './PowerUpCapabilitiesPage.less';

const format = forTemplate('app_management');

export const PowerUpCapabilitiesPage: FunctionComponent<{
  idPlugin: string;
}> = ({ idPlugin }) => {
  const { data, loading } = usePluginCapabilitiesQuery({
    variables: {
      idPlugin,
    },
    waitOn: ['None'],
  });

  const [updatePluginCapabilities] = useUpdatePluginCapabilitiesMutation();
  const [updatePluginCapabilitiesOptions] =
    useUpdatePluginCapabilitiesOptionsMutation();

  const [capabilities, setCapabilities] = useSyncedState(
    data?.plugin?.capabilities || [],
    [],
  );

  const [capabilitiesOptions, setCapabilitiesOptions] = useSyncedState(
    data?.plugin?.capabilitiesOptions || [],
    [],
  );

  const toggleCapability = useCallback(
    async (capability: string) => {
      const traceId = Analytics.startTask({
        taskName: 'edit-plugin/capabilities',
        source: 'powerUpAdminCapabilitiesScreen',
        attributes: {
          idPlugin,
        },
      });
      try {
        const newCapabilities = [...capabilities];
        const indexOfCapability = capabilities.indexOf(capability);
        if (capabilities.includes(capability)) {
          newCapabilities.splice(indexOfCapability, 1);
        } else {
          newCapabilities.push(capability);
        }
        const { data: response } = await updatePluginCapabilities({
          variables: {
            idPlugin,
            capabilities: newCapabilities,
          },
        });
        Analytics.taskSucceeded({
          taskName: 'edit-plugin/capabilities',
          traceId,
          source: 'powerUpAdminCapabilitiesScreen',
          attributes: {
            idPlugin,
          },
        });
        if (response?.updatePlugin) {
          setCapabilities(response.updatePlugin.capabilities);
        }
      } catch (error) {
        Analytics.taskFailed({
          taskName: 'edit-plugin/capabilities',
          traceId,
          source: 'powerUpAdminCapabilitiesScreen',
          attributes: {
            idPlugin,
          },
          error,
        });
      }
    },
    [capabilities, idPlugin, setCapabilities, updatePluginCapabilities],
  );

  // Currently, our only capabilitiesOption is claims-trello for attachment-sections
  const toggleCapabilitiesOptions = useCallback(
    async (option: string) => {
      const newCapabilitiesOptions = capabilitiesOptions.slice();
      const indexOfOption = capabilitiesOptions.indexOf(option);
      if (capabilitiesOptions.includes(option)) {
        newCapabilitiesOptions.splice(indexOfOption, 1);
      } else {
        newCapabilitiesOptions.push(option);
      }

      const { data: response } = await updatePluginCapabilitiesOptions({
        variables: {
          idPlugin,
          capabilitiesOptions: newCapabilitiesOptions,
        },
      });

      if (response?.updatePlugin) {
        setCapabilitiesOptions(response.updatePlugin.capabilitiesOptions);
      }
    },
    [
      capabilitiesOptions,
      idPlugin,
      setCapabilitiesOptions,
      updatePluginCapabilitiesOptions,
    ],
  );

  const { value: powerUpViewsEnabled } = useFeatureGate(
    'ecosystem_power_up_views',
  );

  if (loading) {
    return <LoadingSpinner />;
  }

  const capabilitiesSection = Capabilities.filter((capabilityKey: string) => {
    if (capabilityKey === 'board-views') {
      return powerUpViewsEnabled;
    }
    return true;
  }).map((capabilityKey: string) => (
    <div className={styles.capabilitySection} key={capabilityKey}>
      <div className={styles.nameAndToggle}>
        <div>
          <h2>{format(capabilityKey)}</h2>
          {/* eslint-disable-next-line @atlaskit/design-system/no-html-code */}
          <code>{capabilityKey}</code>
        </div>
        <Toggle
          onChange={(e) => {
            toggleCapability(capabilityKey);
          }}
          checked={capabilities.includes(capabilityKey)}
        />
      </div>
      <div
        className={styles.description}
        // eslint-disable-next-line @eslint-react/dom/no-dangerously-set-innerhtml
        dangerouslySetInnerHTML={{
          __html: format(`${capabilityKey}-description`, {
            pluginName: data?.plugin?.name || '',
          }),
        }}
      />
      <div className={styles.learnMore}>
        <InformationIcon dangerous_className={styles.infoIcon} />
        <a
          target="_blank"
          rel="noopener noreferrer"
          href={`https://developer.atlassian.com/cloud/trello/power-ups/capabilities/${capabilityKey}`}
        >
          {format('learn-more')}
        </a>
      </div>
      {capabilityKey === 'attachment-sections' &&
      capabilities.includes('attachment-sections') ? (
        <div className={styles.capabilitiesOptions}>
          <GearIcon dangerous_className={styles.gearIcon} />
          <div className={styles.nameAndDescription}>
            <h3>{format('claims-trello')}</h3>
            <p
              // eslint-disable-next-line @eslint-react/dom/no-dangerously-set-innerhtml
              dangerouslySetInnerHTML={{
                __html: format('claims-trello-description'),
              }}
            ></p>
          </div>
          <Toggle
            onChange={(e) => {
              toggleCapabilitiesOptions('claims-trello');
            }}
            checked={capabilitiesOptions.includes('claims-trello')}
          />
        </div>
      ) : null}
    </div>
  ));

  return (
    <>
      <div className={styles.header}>
        <h1>{format('capabilities')}</h1>
      </div>
      {capabilitiesSection}
    </>
  );
};
